import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';

// Load SheetJS
let xlsxReady = false;
const loadXlsx = () => new Promise(res => {
  if (xlsxReady || window.XLSX) { xlsxReady = true; res(); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
  s.onload = () => { xlsxReady = true; res(); };
  document.head.appendChild(s);
});

export default function QADownload() {
  const { examId } = useParams();
  const navigate   = useNavigate();
  const [data,     setData]    = useState(null);
  const [loading,  setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/exams/${examId}/download-questions`)
      .then(r => setData(r.data))
      .catch(() => { toast.error('Access denied or exam not found'); navigate(-1); })
      .finally(() => setLoading(false));
  }, [examId, navigate]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!data)   return null;

  const { exam, questions } = data;

  const exportPDF = () => {
    const pw = window.open('', '_blank');
    pw.document.write(buildQAPDF(exam, questions));
    pw.document.close();
    pw.onload = () => { pw.focus(); pw.print(); };
    toast.success('PDF opened for printing');
  };

  const exportExcel = async () => {
    await loadXlsx();
    const XLSX = window.XLSX;
    const headers = ['#','Question','Type','Option A','Option B','Option C','Option D','Option E','Correct Answer','Marks','Explanation'];
    const rows = questions.map((q, i) => {
      const opts = Array.isArray(q.options) ? q.options : (typeof q.options === 'string' ? JSON.parse(q.options||'[]') : []);
      return [
        i+1,
        q.question_text?.replace(/<[^>]+>/g,''),
        q.question_type,
        opts[0]||'', opts[1]||'', opts[2]||'', opts[3]||'', opts[4]||'',
        q.correct_answer||'',
        q.marks,
        q.explanation||''
      ];
    });
    const ws = window.XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [4,50,12,20,20,20,20,20,20,6,30].map(w=>({wch:w}));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Questions & Answers');
    XLSX.writeFile(wb, `QA_${exam.title.replace(/\s+/g,'_')}.xlsx`);
    toast.success('Excel downloaded!');
  };

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <button onClick={()=>navigate(-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', fontSize:13, marginBottom:6, padding:0 }}>← Back</button>
            <h1 className="page-title" style={{ margin:0 }}>📋 Questions & Answers</h1>
            <p className="page-subtitle" style={{ margin:0 }}>{exam.title} · {questions.length} questions</p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary" onClick={exportExcel}>📊 Excel</button>
            <button className="btn btn-primary"   onClick={exportPDF}>📄 PDF</button>
          </div>
        </div>

        {questions.map((q, i) => {
          const opts = Array.isArray(q.options) ? q.options : (typeof q.options === 'string' ? JSON.parse(q.options||'[]') : []);
          const letters = ['A','B','C','D','E'];
          return (
            <div key={q.question_id||i} className="card" style={{ marginBottom:12, borderLeft:`3px solid var(--accent-primary)` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <div style={{ fontWeight:700, fontSize:14, flex:1 }}>
                  Q{i+1}. <span style={{ fontWeight:400 }} dangerouslySetInnerHTML={{ __html: q.question_text }} />
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0, marginLeft:10 }}>
                  <span className="badge badge-blue" style={{ fontSize:11 }}>{q.question_type}</span>
                  <span className="badge badge-purple" style={{ fontSize:11 }}>{q.marks} mark{q.marks!==1?'s':''}</span>
                </div>
              </div>
              {opts.length > 0 && (
                <div style={{ marginBottom:10 }}>
                  {opts.map((opt, j) => {
                    const isCorrect = q.correct_answer === opt;
                    return (
                      <div key={j} style={{ display:'flex', gap:8, alignItems:'center', padding:'5px 10px', borderRadius:6, marginBottom:4, background: isCorrect?'rgba(16,185,129,0.1)':'var(--bg-elevated)', border:`1px solid ${isCorrect?'rgba(16,185,129,0.4)':'var(--border)'}` }}>
                        <span style={{ fontWeight:700, color: isCorrect?'var(--accent-success)':'var(--text-muted)', minWidth:18 }}>{letters[j]}.</span>
                        <span style={{ fontSize:13, flex:1 }}>{opt}</span>
                        {isCorrect && <span style={{ color:'var(--accent-success)', fontWeight:700, fontSize:12 }}>✓ Correct</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <div style={{ padding:'6px 12px', background:'rgba(16,185,129,0.1)', borderRadius:8, border:'1px solid rgba(16,185,129,0.3)' }}>
                  <span style={{ fontSize:11, color:'var(--text-muted)' }}>Answer: </span>
                  <span style={{ fontWeight:700, color:'var(--accent-success)', fontSize:13 }}>{q.correct_answer||'—'}</span>
                </div>
                {q.explanation && (
                  <div style={{ padding:'6px 12px', background:'rgba(79,142,247,0.07)', borderRadius:8, border:'1px solid rgba(79,142,247,0.2)', fontSize:12, color:'var(--text-secondary)' }}>
                    💡 {q.explanation}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
}

function buildQAPDF(exam, questions) {
  const letters = ['A','B','C','D','E'];
  const rows = questions.map((q, i) => {
    const opts = Array.isArray(q.options) ? q.options : (typeof q.options === 'string' ? JSON.parse(q.options||'[]') : []);
    const optHtml = opts.length > 0 ? opts.map((o,j) => {
      const isCorrect = q.correct_answer === o;
      return `<div style="padding:4px 8px;margin:3px 0;border-radius:4px;background:${isCorrect?'#dcfce7':'#f9fafb'};border:1px solid ${isCorrect?'#86efac':'#e5e7eb'}">
        <strong style="color:${isCorrect?'#16a34a':'#9ca3af'}">${letters[j]}.</strong>
        <span style="margin-left:6px">${o}</span>
        ${isCorrect?'<strong style="float:right;color:#16a34a">✓</strong>':''}
      </div>`;
    }).join('') : '';
    return `
      <div style="border:1px solid #ddd;border-radius:6px;padding:12px;margin-bottom:12px;border-left:3px solid #3b82f6;page-break-inside:avoid">
        <div style="display:flex;justify-content:space-between;margin-bottom:8px">
          <strong>Q${i+1}.</strong>
          <span style="font-size:10px;color:#888">${q.question_type} · ${q.marks} mark${q.marks!==1?'s':''}</span>
        </div>
        <div style="margin-bottom:8px;font-size:12px">${q.question_text?.replace(/<[^>]+>/g,'')}</div>
        ${optHtml}
        <div style="margin-top:8px;padding:6px 10px;background:#dcfce7;border-radius:4px;font-size:12px">
          <strong style="color:#166534">✓ Answer:</strong> <span style="color:#15803d;font-weight:700">${q.correct_answer||'—'}</span>
          ${q.explanation?`<div style="color:#555;margin-top:3px;font-size:11px">💡 ${q.explanation}</div>`:''}
        </div>
      </div>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
  <title>Q&A — ${exam.title}</title>
  <style>body{font-family:Arial,sans-serif;font-size:12px;padding:20px;color:#111}h1{font-size:20px;margin-bottom:4px}.sub{color:#555;margin-bottom:20px}@media print{@page{margin:12mm}}</style>
  </head><body>
  <h1>Questions & Correct Answers</h1>
  <div class="sub">${exam.title} · ${questions.length} questions · Generated ${new Date().toLocaleString()}</div>
  ${rows}
  </body></html>`;
}
